import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { toast } from 'react-hot-toast';
import DataTable from 'react-data-table-component';

import Modal from './Modal';
import SectionHeader from './SectionHeader';
import TableLoader from './TableLoader';
import ConfirmPrompt from './ConfirmPrompt';
import AddPotentialHireForm from './AddPotentialHireForm';
import UpdatePotentialHireForm from './UpdatePotentialHireForm';
import PotentialHireEmailForm from './PotentialHireEmailForm';
import EmailResultModal from './EmailResultModal';
import FilterSelect from './FilterSelect';

import usePotentialHires from '../hooks/usePotentialHires';
import usePotentialHireCategories from '../hooks/usePotentialHireCategories';
import useDebounce from '../hooks/useDebounce';

import potentialHireService from '../services/potentialHireService';

import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const PotentialHires = () => {
  const [selectedHire, setSelectedHire] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleClearRows, setToggleClearRows] = useState(false);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => { setPage(1); }, [search, selectedCategory]);

  const { data: categoriesData, isLoading: categoriesLoading, mutate: mutateCategories } = usePotentialHireCategories();
  const categoryOptions = (categoriesData || []).map(cat => ({ value: cat, label: cat }));

  const { data, isLoading, mutate } = usePotentialHires({ page, perPage, sortField, sortOrder, search, category: selectedCategory?.value });
  const hires = data?.hires;
  const hireCount = data?.count;

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [isEmailResultOpen, setIsEmailResultOpen] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const handleRowClicked = (row) => {
    setSelectedHire(row);
    setIsUpdateOpen(true);
  };

  const handleDeleteClicked = (hire) => {
    setSelectedHire(hire);
    setIsDeleteOpen(true);
  };

  const handleSelectedRowsChange = ({ selectedRows }) => {
    setSelectedRows(selectedRows);
  };

  const handleSort = (column, sortDirection) => {
    setSortField(column.sortField);
    setSortOrder(sortDirection === 'desc' ? 1 : -1);
  };

  const handleCreate = async (data) => {
    try {
      setIsCreating(true);
      const { hire } = await potentialHireService.createPotentialHire(data);
      await Promise.all([mutate(), mutateCategories()]);
      setIsAddOpen(false);
      toast.success(`${hire.fullName} added`);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to add potential hire');
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdate = async (data) => {
    try {
      setIsUpdating(true);
      const { hire } = await potentialHireService.updatePotentialHire(selectedHire._id, data);
      await Promise.all([mutate(), mutateCategories()]);
      setIsUpdateOpen(false);
      setSelectedHire(null);
      toast.success(`${hire.fullName} updated`);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update potential hire');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await potentialHireService.deletePotentialHire(selectedHire._id);
      await Promise.all([mutate(), mutateCategories()]);
      setIsDeleteOpen(false);
      setSelectedHire(null);
      toast.success('Potential hire deleted');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete potential hire');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSendEmail = async ({ subject, content }) => {
    try {
      setIsSendingEmail(true);
      const ids = selectedRows.map(r => r._id);
      const result = await potentialHireService.emailPotentialHires({ ids, subject, content });
      setIsEmailOpen(false);
      setToggleClearRows(t => !t);
      setSelectedRows([]);

      if (result.failed.length > 0) {
        setEmailResult(result);
        setIsEmailResultOpen(true);
      } else {
        toast.success(`Email sent to ${result.sent.length} ${result.sent.length === 1 ? 'person' : 'people'}`);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to send email');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const columns = [
    {
      name: 'Name',
      selector: row => row.fullName,
      sortable: true,
      sortField: 'fullName'
    },
    {
      name: 'Email',
      selector: row => row.email,
      sortable: true,
      sortField: 'email'
    },
    {
      name: 'Phone',
      selector: row => row.phoneNumber,
      sortable: true,
      sortField: 'phoneNumber'
    },
    {
      name: 'Category',
      selector: row => row.category || '—',
      sortable: true,
      sortField: 'category'
    },
    {
      name: 'Notes',
      selector: row => row.notes || '—',
      grow: 2
    },
    {
      right: 'true',
      cell: row => (
        <button
          onClick={(e) => { e.stopPropagation(); handleDeleteClicked(row); }}
          className="py-1 px-2 text-xs font-semibold cursor-pointer rounded-md bg-red-600 text-white text-nowrap"
        >
          Delete
        </button>
      )
    }
  ];

  const bulkActions = selectedRows.length > 0 && (
    <div className="flex items-center gap-2">
      <span className="text-xs text-white">{selectedRows.length} selected</span>
      <button
        onClick={() => setIsEmailOpen(true)}
        className="px-2 py-1 bg-white rounded-md text-ase-blue font-semibold text-xs"
      >
        Send Email
      </button>
      <button
        disabled
        title="SMS coming soon"
        className="px-2 py-1 bg-white rounded-md text-ase-blue font-semibold text-xs opacity-40 cursor-not-allowed"
      >
        Send SMS
      </button>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Potential Hires | ASE Group</title>
      </Helmet>

      <div className="border border-ase-blue rounded-lg pb-1">
        <SectionHeader
          title="Potential Hires"
          actionsRightButtonText="Filters"
          actionsRight={
            <div className="flex flex-col md:flex-row md:items-center gap-2 w-full">
              <div className="relative flex items-center w-full md:w-auto">
                <MagnifyingGlassIcon
                  className="absolute left-[10px] h-3 w-3 text-gray-600 pointer-events-none"
                />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  className="!pl-[28px] pr-2 text-xs !h-[25px] !border border-none rounded-md bg-white text-gray-700 placeholder-gray-400 focus:outline-none w-full md:w-48"
                />
              </div>

              <p className="text-white text-xs">Category:</p>
              <div className="text-xs h-[24px]">
                <FilterSelect
                  options={categoryOptions}
                  value={selectedCategory}
                  onChange={setSelectedCategory}
                  placeholder="All categories"
                  isClearable
                />
              </div>
            </div>
          }
          mobileActionsBelow={bulkActions}
          actionsLeft={
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsAddOpen(true)}
                className="px-2 py-1 bg-white rounded-md text-ase-blue font-semibold text-xs"
              >
                Add
              </button>

              <div className="hidden md:flex items-center gap-2">
                {bulkActions}
              </div>
            </div>
          }
        />

        <DataTable
          columns={columns}
          data={hires}
          striped
          highlightOnHover
          pointerOnHover
          onRowClicked={handleRowClicked}
          progressPending={isLoading}
          progressComponent={<TableLoader />}
          sortServer
          onSort={handleSort}
          pagination
          paginationServer
          paginationTotalRows={hireCount}
          paginationRowsPerPageOptions={[10, 20, 50]}
          paginationPerPage={perPage}
          onChangePage={setPage}
          onChangeRowsPerPage={(newPerPage, page) => { setPerPage(newPerPage); setPage(page); }}
          selectableRows
          onSelectedRowsChange={handleSelectedRowsChange}
          clearSelectedRows={toggleClearRows}
        />
      </div>

      <Modal isOpen={isAddOpen} overflowType="visible">
        <AddPotentialHireForm
          isLoading={isCreating}
          onSubmit={handleCreate}
          onCloseClicked={() => setIsAddOpen(false)}
          categoryOptions={categoryOptions}
          categoriesLoading={categoriesLoading}
        />
      </Modal>

      <Modal isOpen={isUpdateOpen} overflowType="visible">
        <UpdatePotentialHireForm
          hire={selectedHire}
          isLoading={isUpdating}
          onSubmit={handleUpdate}
          onCloseClicked={() => { setIsUpdateOpen(false); setSelectedHire(null); }}
          categoryOptions={categoryOptions}
          categoriesLoading={categoriesLoading}
        />
      </Modal>

      <Modal isOpen={isDeleteOpen}>
        <ConfirmPrompt
          message={`Are you sure you want to delete ${selectedHire?.fullName}?`}
          onConfirm={handleDelete}
          onCancel={() => { setIsDeleteOpen(false); setSelectedHire(null); }}
          isLoading={isDeleting}
        />
      </Modal>

      <Modal isOpen={isEmailOpen}>
        <PotentialHireEmailForm
          selectedCount={selectedRows.length}
          isLoading={isSendingEmail}
          onSubmit={handleSendEmail}
          onCloseClicked={() => setIsEmailOpen(false)}
        />
      </Modal>

      <Modal isOpen={isEmailResultOpen}>
        {emailResult && (
          <EmailResultModal
            result={emailResult}
            onClose={() => setIsEmailResultOpen(false)}
          />
        )}
      </Modal>
    </>
  );
};

export default PotentialHires;
